/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-unused-vars */
import React from 'react';
import axios, { API } from 'lib/config/axios';
import { useTranslation } from 'next-i18next';

// Material UI Core
import { styled } from '@mui/material/styles';
import {
  Button,
  CardMedia,
  CardContent,
  Divider,
  Card,
  Box,
  Container,
  Hidden,
  Grid,
  Typography,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { useSelector, useDispatch } from 'react-redux';
import { setSnackbar } from 'redux/features/ui-slice';
import LightTextField from 'views/common/components/UI/LightTextField';
import DatePicker from 'views/common/components/UI/DatePicker';
import NavigatorLink from 'views/common/components/Logical/NavigatorLink';
// Material UI icons
import HelpIcon from '@mui/icons-material/Help';
import getConfig from 'next/config';
import dayjs from 'dayjs';
import PATHS from 'lib/navigation/paths';
import { AppAdLinks } from 'views/CheckInView/AppAdLinks';
import { FAQS_TYPES_PATHS } from 'lib/utils/CONSTANTS';
import Typography500 from 'views/common/components/UI/Typography500';
import InfoIcon from '@mui/icons-material/Info';
import { ImgBox } from 'views/common/components';

const PREFIX = 'CheckinView';

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  cover: `${PREFIX}-cover`,
  mainTitle: `${PREFIX}-mainTitle`,
  form: `${PREFIX}-form`,
  lookup: `${PREFIX}-lookup`,
  divider: `${PREFIX}-divider`,
  footerWrapper: `${PREFIX}-footerWrapper`,
  infoNote: `${PREFIX}-infoNote`,
  smallNote: `${PREFIX}-smallNote`,
  footer: `${PREFIX}-footer`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.root}`]: { display: 'flex' },

  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(3),
  },

  [`& .${classes.mainTitle}`]: {
    marginBottom: theme.spacing(4),
  },

  [`& .${classes.form}`]: {
    paddingBottom: theme.spacing(2),
    "& > [class*='MuiGrid-container']": {
      marginTop: theme.spacing(-1),
      marginBottom: theme.spacing(-1),
    },
  },

  [`& .${classes.divider}`]: {
    position: 'relative',
    margin: theme.spacing(2.2, 0),
    '& span': {
      position: 'absolute',
      padding: '0 10px',
      top: '50%',
      textTransform: 'uppercase',
      backgroundColor: theme.palette.common.white,
      color: theme.palette.grey[200],
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },

  [`& .${classes.footerWrapper}`]: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    justifyContent: 'flex-start',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },

  [`& .${classes.infoNote}`]: {
    background: theme.palette.grey[50],
    color: theme.palette.common.black,
    borderRadius: theme.spacing(2),
    '& .CVInfoNoteIcon': {
      color: theme.palette.grey[300],
    },
  },

  [`& .${classes.footer}`]: {
    display: 'flex',
    gap: theme.spacing(1.25),
    color: theme.palette.common.black,

    '& a': {
      textTransform: 'uppercase',
      textDecoration: 'underline',
    },
    '& .CVicon': {
      color: theme.palette.grey[300],
    },
  },
  '& .CVBannerContainer': {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  '& .CVBanner': {
    width: '100%',
    flex: 1,
    '& img': {
      objectPosition: 'bottom',
    },
  },
  '& .CVBannerFooter': {
    position: 'relative',
    height: theme.typography.pxToRem(226),
    display: 'flex',
    alignItems: 'center',
  },
  '& .CNBannerFooterrBG': {
    width: '100%',
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: -1,
  },
}));

const CheckinView = ({ props }) => {
  const appState = useSelector((state) => state.globalData.appState);

  const { t } = useTranslation([
    'fe_er_check_in',
    'controllers',
    'fe_er_check_in',
  ]);
  const handleError = useErrorsHandler();
  const theme = useTheme();

  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const {
    publicRuntimeConfig: { envVars },
  } = getConfig();
  const currentUser = appState.current_user;

  const dispatch = useDispatch();
  const appNavigator = useAppNavigator();

  const initialState = {
    form1: {
      first_name: '',
      last_name: '',
      confirmation_number: '',
      middle_name: '',
    },
    form2: {
      first_name: '',
      last_name: '',
      middle_name: '',
      pickup_location_id: 4,
      pickup_date: new Date(),
    },
    eagleShare: {
      confirmation_number: '',
      email: currentUser && !currentUser.privileged ? currentUser.email : '',
    },
  };

  const [open, setOpen] = React.useState(false);

  const [value1, setValue1] = React.useState(initialState.form1); // 1st form

  const [value2, setValue2] = React.useState(initialState.form2); // 2nd form
  const [eagleShareForm, setEagleShareForm] = React.useState(
    initialState.eagleShare,
  ); // 2nd form

  const [errorMsgs, setErrorMsgs] = React.useState({
    form1: {},
    form2: {},
    eagleShare: {},
  });

  const handlePickupDateChange = (date) => {
    setValue2({
      ...value2,
      pickup_date: date,
    });
  };

  const handleInputChange1 = (e) => {
    setValue1({
      ...value1,
      [e.target.name]: e.target.value,
    });
  };
  const handleEagleShareForm = (e) => {
    setEagleShareForm({
      ...eagleShareForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleInputChange2 = (e) => {
    setValue2({
      ...value2,
      [e.target.name]: e.target.value,
    });
  };

  const handleAutocompleteChange = (event, newInputValue) => {
    setValue2({
      ...value2,
      pickup_location_id: newInputValue ? newInputValue.id : null,
    });
  };

  const [pickupLocations, setPickupLocations] = React.useState([]);

  React.useEffect(() => {
    async function fetchData() {
      try {
        const { data } = await axios.get(API.vrs.lookup);
        if (data?.pickup_regions) {
          let pickups = [];
          // Pass it as a regular array of object and Let MuiAutocomplete categorize them using groupBy
          data.pickup_regions.forEach((region) => {
            pickups = pickups.concat(
              region.locations.map((location) => ({
                ...location,
                category: region.name,
              })),
            );
          });
          setPickupLocations(pickups);
        }
      } catch (error) {
        handleError(error);
      }
    }
    fetchData();
  }, []);

  const handleSubmit = async (e, form1) => {
    e.preventDefault();
    setErrorMsgs({
      form1: {},
      form2: {},
      eagleShare: {},
    });
    const submitedData = {
      ...(form1 ? value1 : value2),
      ...(form1
        ? {}
        : {
          pickup_date: dayjs(value2.pickup_date).format('YYYY-MM-DD'),
        }),
    };

    axios
      .post(API.vrs.lookup, {
        authenticity_token: appState.authenticity_token,
        lookup: submitedData,
      })
      .then((res) => {
        if (res.data.vehicle_reservation_identifier) {
          appNavigator.push(
            PATHS.vrs.checkIn(res.data.vehicle_reservation_identifier),
          );
        }
      })
      .catch((error) => {
        try {
          switch (error.response.status) {
            case 404:
            case 406:
              dispatch(
                setSnackbar({
                  open: true,
                  severity: 'error',
                  message: error.response.data.message,
                }),
              );
              break;
            default:
              handleError(error);
          }
        } catch (err) {
          handleError(error);
        }
      });
  };

  const esForm = (
    <form onSubmit={(e) => handleSubmit(e, true)} className={classes.form}>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <LightTextField
            required
            error={Boolean(errorMsgs.confirmation_number)}
            fullWidth
            color="secondary"
            label={t('fe_er_check_in:booking_confirmation_number')}
            variant="outlined"
            name="confirmation_number"
            value={eagleShareForm.confirmation_number}
            onChange={handleEagleShareForm}
            helperText={errorMsgs.confirmation_number}
            inputProps={{ 'data-testid': 'checkin-confirmation-number' }}
          />
        </Grid>
        <Grid item md={6} sm={12}>
          <LightTextField
            required
            error={Boolean(errorMsgs.email)}
            fullWidth
            color="secondary"
            label={t('fe_er_check_in:primary_rider_email')}
            variant="outlined"
            name="email"
            value={eagleShareForm.email}
            onChange={handleEagleShareForm}
            helperText={errorMsgs.email}
            disabled={initialState.eagleShare.email !== ''}
            inputProps={{ 'data-testid': 'checkin-email' }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={4} justifyContent="flex-end">
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.lookup}
            data-testid="checkin-lookup-btn">
            {t('fe_er_check_in:look_up')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );

  const form1 = (
    <form onSubmit={(e) => handleSubmit(e, true)} className={classes.form}>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <LightTextField
            required
            error={Boolean(errorMsgs.confirmation_number)}
            fullWidth
            color="secondary"
            label={t('fe_er_check_in:booking_confirmation_number')}
            variant="outlined"
            name="confirmation_number"
            value={value1.confirmation_number}
            onChange={handleInputChange1}
            helperText={errorMsgs.confirmation_number}
            inputProps={{ 'data-testid': 'checkin-confirmation-number' }}
          />
        </Grid>
        <Grid item md={6} sm={12}>
          <LightTextField
            required
            error={Boolean(errorMsgs.last_name)}
            fullWidth
            color="secondary"
            label={t('fe_er_check_in:last_name')}
            variant="outlined"
            name="last_name"
            value={value1.last_name}
            onChange={handleInputChange1}
            helperText={errorMsgs.last_name}
            inputProps={{ 'data-testid': 'checkin-last-name' }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <LightTextField
            error={Boolean(errorMsgs.first_name)}
            fullWidth
            color="secondary"
            label={t('fe_er_check_in:first_name')}
            variant="outlined"
            name="first_name"
            value={value1.first_name}
            onChange={handleInputChange1}
            helperText={errorMsgs.first_name}
          />
        </Grid>
        <Grid item md={6} sm={12}>
          <LightTextField
            error={Boolean(errorMsgs.middle_name)}
            color="secondary"
            label={t('fe_er_check_in:middle_name')}
            variant="outlined"
            fullWidth
            name="middle_name"
            value={value1.middle_name}
            onChange={handleInputChange1}
            helperText={errorMsgs.middle_name}
          />
        </Grid>
      </Grid>
      <Grid container spacing={4} justifyContent="flex-end">
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.lookup}
            data-testid="checkin-lookup-btn">
            {t('fe_er_check_in:look_up')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );

  const form2 = (
    <form onSubmit={(e) => handleSubmit(e, false)} className={classes.form}>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <LightTextField
            required
            error={Boolean(errorMsgs.first_name)}
            fullWidth
            color="secondary"
            label={t('fe_er_check_in:first_name')}
            variant="outlined"
            name="first_name"
            value={value2.first_name}
            onChange={handleInputChange2}
            helperText={errorMsgs.first_name}
          />
        </Grid>
        <Grid item md={6} sm={12}>
          <LightTextField
            required
            error={Boolean(errorMsgs.last_name)}
            fullWidth
            color="secondary"
            label={t('fe_er_check_in:last_name')}
            variant="outlined"
            name="last_name"
            value={value2.last_name}
            onChange={handleInputChange2}
            helperText={errorMsgs.last_name}
          />
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <LightTextField
            error={Boolean(errorMsgs.middle_name)}
            color="secondary"
            label={t('fe_er_check_in:middle_name')}
            variant="outlined"
            fullWidth
            name="middle_name"
            value={value2.middle_name}
            onChange={handleInputChange2}
            helperText={errorMsgs.middle_name}
          />
        </Grid>
        <Grid item md={6} sm={12}>
          <Autocomplete
            required
            fullWidth
            id="pick-up-city"
            options={pickupLocations}
            groupBy={(option) => option.category}
            getOptionLabel={(option) => option.name}
            style={{ width: '100%' }}
            name="pickup_location_id"
            onChange={handleAutocompleteChange}
            renderInput={(params) => (
              <LightTextField
                {...params}
                required
                error={Boolean(errorMsgs.pickup_location_id)}
                label={t('fe_er_check_in:pick_up_city')}
                variant="outlined"
                helperText={errorMsgs.pickup_location_id}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item md={6} sm={12}>
          <DatePicker
            required
            closeOnSelect
            format="MM/DD/YYYY"
            margin="normal"
            name="pickup_date"
            id="pick-up-date-picker"
            value={dayjs(value2.pickup_date)}
            onChange={handlePickupDateChange}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            label={t('fe_er_check_in:pick_up_date')}
            slotProps={{
              textField: {
                helperText: errorMsgs.pickup_date,
                error: Boolean(errorMsgs.pickup_date),
                fullWidth: true,
                required: true,
                onClick: (e) => setOpen(true),
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={4} justifyContent="flex-end">
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.lookup}>
            {t('fe_er_check_in:look_up')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
  return (
    <StyledBox>
      <Grid container spacing={3} className={classes.root}>
        {!isTablet && (
          <Grid item lg={4}>
            <Box className="CVBannerContainer">
              <Box className="CVBanner">
                <ImgBox
                  image={{
                    src: '/imgs/side_banner.png',
                    alt: 'ER-Banner',
                  }}
                  imgWidth="100%"
                  imgHeight="100%"
                />
              </Box>

              <Box className="CVBannerFooter">
                <AppAdLinks />
                <Box className="CNBannerFooterrBG">
                  <ImgBox
                    image={{
                      src: '/imgs/AppAdBG.png',
                      alt: 'Ad-BG',
                    }}
                    imgHeight="100%"
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
        )}

        <Grid item lg={8} md={12} sm={12} className={classes.content}>
          <Container>
            <Typography variant="h3" className={classes.mainTitle}>
              {t('fe_er_check_in:find_your_booking')}
            </Typography>
            <Alert
              severity="info"
              className={classes.infoNote}
              color="secondary"
              icon={<InfoIcon className="CVInfoNoteIcon" />}>
              {t('fe_er_check_in:checkin_required')}
              {' '}
              {t('fe_er_check_in:checkin_failure')}
            </Alert>

            <>
              {form1}
              <Divider
                className={classes.divider}
                component="div"
                variant="fullWidth">
                <span>{t('fe_er_check_in:or')}</span>
              </Divider>
              {form2}
            </>

            <div className={classes.footerWrapper}>
              <div className={classes.footer}>
                <HelpIcon className="CVicon" />
                <Box>
                  <Typography variant="body2" mb={0.75}>
                    {t('fe_er_check_in:q_about_rental')}
                  </Typography>
                  <Typography500 variant="body2">
                    <NavigatorLink
                      href={PATHS.faqs.withSlug(FAQS_TYPES_PATHS.rental)}>
                      &nbsp;
                      {t('fe_er_check_in:see_our_faqs')}
                    </NavigatorLink>
                  </Typography500>
                </Box>
              </div>
            </div>
          </Container>
        </Grid>
      </Grid>
    </StyledBox>
  );
};
export default CheckinView;
